import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string';
import {
	DocumentFilter,
	DocumentFolderEntityMap,
	DocumentLinkTarget,
	DownloadPreSignedUrlDto,
	EditDocumentDto,
	FindRelatedToFilter,
	PreSignedUrlPostAWSHeaders,
	PreSignedUrlPostBody,
	PreSignedUrlResponse,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	CreateDocumentsDto,
	DealClassifierRes,
	FindRelatedDocumentsRes,
	FindRelatedFileDto,
	FindRelatedFilesRes,
	GetDocumentRes,
	GetDocumentsRes,
	UpdateRelatedDocumentDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc';
import { cleanFileName } from '@dealstack/shared/lib/common/file/file';

export class DocumentService {
	static createPreSignedUrls = async ({
		fileData,
		entityMap,
		dealId,
	}: {
		fileData: PreSignedUrlPostBody;
		entityMap?: DocumentFolderEntityMap;
		dealId: string;
	}): Promise<PreSignedUrlResponse[]> => {
		const response = await makeRequest<{ payload: PreSignedUrlResponse[] }>({
			endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_UPLOAD_PRE_SIGNED_URLS_POST, {
				dealId,
			}),
			method: HTTP_METHOD.POST,
			body: JSON.stringify({ fileData, entityMap }),
		});
		return response.payload;
	};

	static putUploadToPreSignedUrl = async ({
		preSignedUrl,
		file,
	}: {
		preSignedUrl: string;
		file: File;
	}): Promise<string> => {
		const fileName = cleanFileName(file.name);
		const response = await fetch(preSignedUrl, {
			method: HTTP_METHOD.PUT,
			headers: {
				'Content-Type': file.type,
				'Content-Disposition': `attachment; filename="${fileName}"`,
			},
			body: file,
		});

		if (!response.ok) {
			throw new Error('failed to upload');
		}

		// this is the MD5 hash of the file returned from aws
		// we use this to make sure the file uploaded and the one stored is the same
		const eTag = response.headers.get('ETag') as PreSignedUrlPostAWSHeaders['eTag'];

		return eTag;
	};

	static getFileExtensionFromName = (fileName: string) => {
		return fileName.split('.').pop() || '';
	};

	static findRelatedFiles = async ({
		dealId,
		bodyPayload,
	}: {
		dealId: string;
		bodyPayload: FindRelatedFileDto;
	}) => {
		const response = await makeRequest<{ payload?: FindRelatedFilesRes[] }>({
			endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_FIND_RELATED_FILES_POST, {
				dealId,
			}),
			method: HTTP_METHOD.POST,
			body: JSON.stringify(bodyPayload),
		});

		return response?.payload;
	};

	static createDocuments = async ({
		dealId,
		fileData,
		linkTarget,
		documentsToLink,
	}: Omit<CreateDocumentsDto, 'linkTarget'> & {
		dealId: string;
		linkTarget?: DocumentLinkTarget;
	}): Promise<string> => {
		const response = await makeRequest<{ id: string }>({
			endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_CREATE_POST, {
				dealId,
			}),
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				fileData,
				dealId,
				linkTarget,
				documentsToLink,
			}),
		});

		return response.id;
	};

	static updateDocument = async ({
		dealId,
		documentId,
		updateData,
	}: {
		updateData: EditDocumentDto;
		documentId: string;
		dealId: string;
	}): Promise<string> => {
		const response = await makeRequest<{ id: string }>({
			endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_PUT, {
				dealId,
				documentId,
			}),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({ updateData }),
		});

		return response.id;
	};

	static getDocument = async (
		endpoint: string,
		args: {
			transactionId?: string;
		},
		signal?: AbortSignal
	) => {
		const response = await makeRequest<ApiV1.Response<GetDocumentRes>>(
			{
				endpoint,
				method: HTTP_METHOD.GET,
				queryString: {
					transactionId: args.transactionId,
				},
			},
			{
				abortSignal: signal,
			}
		);
		return response;
	};

	static getDealClassifiers = async (endpoint: string, signal?: AbortSignal) => {
		const response = await makeRequest<ApiV1.Response<DealClassifierRes[]>>(
			{
				endpoint,
				method: HTTP_METHOD.GET,
			},
			{
				abortSignal: signal,
			}
		);
		return response;
	};

	static getDocuments = async (
		endpoint: string,
		filters: DocumentFilter,
		signal?: AbortSignal
	) => {
		const fromDate = filters?.fieldFilter?.updatedAt?.from;
		const toDate = filters?.fieldFilter?.updatedAt?.to;
		if (fromDate && filters?.fieldFilter?.updatedAt) {
			filters.fieldFilter.updatedAt.from = browserDateToMidnightUTC(fromDate);
		}
		if (toDate && filters?.fieldFilter?.updatedAt) {
			filters.fieldFilter.updatedAt.to = browserDateToMidnightUTC(toDate);
		}

		const response = await makeRequest<ApiV1.Response<GetDocumentsRes>>(
			{
				endpoint,
				method: HTTP_METHOD.GET,
				queryString: filters,
			},
			{
				abortSignal: signal,
			}
		);
		return response;
	};

	static removeDocument = async (data: { dealId: string; documentId: string }) => {
		const response = await makeRequest<ApiV1.Response<GetDocumentsRes[]>>({
			endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_DELETE, {
				dealId: data.dealId,
				documentId: data.documentId,
			}),
			method: HTTP_METHOD.DELETE,
		});
		return response;
	};

	static removeDocumentPersonLink = async (data: {
		dealId: string;
		documentId: string;
		personId: string;
	}) => {
		const response = await makeRequest<ApiV1.Response<GetDocumentsRes[]>>({
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_PERSON_PERSONID_DELETE,
				{
					dealId: data.dealId,
					documentId: data.documentId,
					personId: data.personId,
				}
			),
			method: HTTP_METHOD.DELETE,
		});
		return response;
	};

	static removeDocumentTransactionLink = async (data: {
		dealId: string;
		documentId: string;
		transactionId: string;
	}) => {
		const response = await makeRequest<ApiV1.Response<GetDocumentsRes[]>>({
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_TRANSACTION_TRANSACTIONID_DELETE,
				{
					dealId: data.dealId,
					documentId: data.documentId,
					transactionId: data.transactionId,
				}
			),
			method: HTTP_METHOD.DELETE,
		});
		return response;
	};

	static getDownloadPreSignedUrl = async ({
		dealId,
		organizationId,
		fileId,
		fileName,
	}: {
		dealId?: string;
		organizationId?: string;
		fileId: string;
		fileName?: string;
	}) => {
		const endpoint = organizationId
			? formatUnicorn(
					API_PATHS.PROGRAMME_DEFAULTS_FILE_ORGANIZATIONID_FILEID_DOWNLOAD_PRE_SIGNED_URL_POST,
					{ organizationId, fileId }
				)
			: formatUnicorn(API_PATHS.FILE_ID_DEAL_DEALID_DOWNLOAD_PRE_SIGNED_URL_POST, {
					dealId: defaultToEmptyString(dealId),
					id: fileId,
				});

		const response = await makeRequest<ApiV1.Response<DownloadPreSignedUrlDto>>({
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({ fileName }),
		});
		return response;
	};

	static findRelatedTo = async (
		endpoint: string,
		filters: FindRelatedToFilter,
		signal?: AbortSignal
	) => {
		const response = await makeRequest<ApiV1.Response<FindRelatedDocumentsRes[]>>(
			{
				endpoint,
				method: HTTP_METHOD.GET,
				queryString: filters,
			},
			{
				abortSignal: signal,
			}
		);
		return response;
	};

	static updateTransactionRelatedDocuments = async ({
		dealId,
		updateData,
		documentId,
	}: {
		updateData: UpdateRelatedDocumentDto[];
		dealId: string;
		documentId: string;
	}) => {
		const endpoint = formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_RELATED_POST, {
			dealId,
			documentId,
		});

		const response = await makeRequest<ApiV1.Response<FindRelatedDocumentsRes[]>>({
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(updateData),
		});
		return response;
	};
}
